import type { PlannerSuccessMetric } from "../state/usePlannerStore";

/**
 * Sticky header that pins the play's desired outcome + success metrics
 * across the planning lifecycle (Editor → Review → running PlayWorkspace).
 *
 * Renders nothing when neither outcome nor metrics are set — earlier
 * plays predate the field and the header would be a confusing empty
 * strip. Once any one of them is present, the strip appears.
 *
 * Data flows from two equivalent sources:
 *   - planner store's PlannerSession (during Editor / Review, before
 *     the play row exists)
 *   - PlayRow on a running play (PlayWorkspace, after launch)
 * Both shapes share the same fields, so the component just accepts
 * `outcomeStatement: string | null` + `successMetrics: PlannerSuccessMetric[]`.
 */
export function OutcomeHeader({
  outcomeStatement,
  successMetrics,
  variant = "default",
}: {
  outcomeStatement: string | null;
  successMetrics: PlannerSuccessMetric[] | null | undefined;
  /** "default" is the full strip; "compact" is a single line for tight surfaces. */
  variant?: "default" | "compact";
}) {
  const metrics = successMetrics ?? [];
  if (!outcomeStatement && metrics.length === 0) return null;

  if (variant === "compact") {
    return (
      <div
        data-testid="outcome-header"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "6px 14px",
          borderBottom: "1px solid var(--border)",
          background: "var(--bg-soft)",
          fontSize: 12,
          color: "var(--text-2)",
          flexWrap: "wrap",
        }}
      >
        <span style={{ fontWeight: 600, color: "var(--text)" }}>
          🎯 {outcomeStatement ?? "(no outcome stated)"}
        </span>
        {metrics.length > 0 && (
          <>
            <span style={{ color: "var(--text-3)" }}>·</span>
            {metrics.map((m, i) => (
              <MetricChip key={`${m.label}-${i}`} m={m} compact />
            ))}
          </>
        )}
      </div>
    );
  }

  return (
    <div
      data-testid="outcome-header"
      style={{
        padding: "10px 16px",
        borderBottom: "1px solid var(--border)",
        background: "color-mix(in srgb, var(--accent, #6366f1) 4%, transparent)",
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "var(--text-3)",
          marginBottom: 4,
        }}
      >
        Outcome
      </div>
      <div
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: "var(--text)",
          lineHeight: 1.35,
        }}
      >
        {outcomeStatement ?? <em style={{ color: "var(--text-3)", fontWeight: 400 }}>(no outcome stated — capture it on the chooser)</em>}
      </div>
      {metrics.length > 0 && (
        <div
          style={{
            marginTop: 6,
            display: "flex",
            flexWrap: "wrap",
            gap: 6,
          }}
        >
          {metrics.map((m, i) => (
            <MetricChip key={`${m.label}-${i}`} m={m} />
          ))}
        </div>
      )}
    </div>
  );
}

function MetricChip({
  m,
  compact,
}: {
  m: PlannerSuccessMetric;
  compact?: boolean;
}) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "baseline",
        gap: 4,
        padding: compact ? "2px 6px" : "3px 8px",
        borderRadius: 10,
        background: "var(--bg)",
        border: "1px solid var(--border)",
        fontSize: compact ? 11 : 12,
        color: "var(--text-2)",
      }}
    >
      <span style={{ color: "var(--text-3)" }}>{m.label}</span>
      <span style={{ fontWeight: 600, color: "var(--text)" }}>{m.target}</span>
      {m.measurement_window && (
        <span style={{ color: "var(--text-3)" }}>· {m.measurement_window}</span>
      )}
    </span>
  );
}
